import { AlarmClock, Bookmark, BookmarkFilled } from '@/components/ui/icons';
import { useEffect, useState, type RefObject } from 'react';
import { useNavigate } from 'react-router';
import type { SavedMessageDTO } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { savedApi } from '@/api/endpoints';
import { MessageContent } from '@/components/chat/MessageContent';
import { Avatar } from '@/components/ui/Avatar';
import { IconButton } from '@/components/ui/Button';
import { EmptyState, ErrorState } from '@/components/ui/Feedback';
import { Popover } from '@/components/ui/Popover';
import { Spinner } from '@/components/ui/Spinner';
import { formatReminder, toggleSaved } from '@/lib/saved';
import { formatMessageTime } from '@/lib/time';
import { targetKey } from '@/stores/messages';
import { useSaved } from '@/stores/saved';
import { useUi } from '@/stores/ui';
import { useUser } from '@/stores/users';

/** Your saved messages (top bar → bookmark): jump to them, set reminders, or let them go. */
export function SavedPanel({ open, onClose, anchor }: { open: boolean; onClose: () => void; anchor: RefObject<HTMLElement | null> }) {
  const [items, setItems] = useState<SavedMessageDTO[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const savedIds = useSaved((s) => s.byId);

  useEffect(() => {
    if (!open) return;
    setError(null);
    savedApi
      .list()
      .then(setItems)
      .catch((err) => setError(errorMessage(err)));
  }, [open]);

  // Unsaved (here or on another device) disappears straight away; reminders show as they are now.
  const shown = (items ?? []).filter((i) => i.messageId in savedIds).map((i) => ({ ...i, remindAt: savedIds[i.messageId] ?? null }));

  return (
    <Popover open={open} onClose={onClose} anchor={anchor} placement="bottom-end" label="Saved messages" className="flex w-[min(420px,calc(100vw-16px))] flex-col">
      <div className="border-b border-line px-4 py-3">
        <h2 className="font-bold">Saved messages</h2>
        <p className="text-xs text-fg-muted">Only you can see these. Use “Remind me” on a message to get a nudge later.</p>
      </div>
      <div className="max-h-[min(520px,70dvh)] overflow-y-auto p-1.5">
        {error ? (
          <ErrorState message={error} onRetry={() => savedApi.list().then(setItems, (err) => setError(errorMessage(err)))} />
        ) : !items ? (
          <div className="flex justify-center py-10 text-fg-muted">
            <Spinner />
          </div>
        ) : shown.length === 0 ? (
          <EmptyState icon={<Bookmark size={26} />} title="Nothing saved yet" body="Open a message’s menu and pick Save message, or Remind me…" />
        ) : (
          <ul className="flex flex-col gap-1">
            {shown.map((item) => (
              <SavedItem key={item.messageId} item={item} onNavigate={onClose} />
            ))}
          </ul>
        )}
      </div>
    </Popover>
  );
}

function SavedItem({ item, onNavigate }: { item: SavedMessageDTO; onNavigate: () => void }) {
  const { message, where } = item;
  const author = useUser(message.author?.id, message.author);
  const navigate = useNavigate();
  const requestJump = useUi((s) => s.requestJump);
  const openModal = useUi((s) => s.openModal);

  const jump = () => {
    onNavigate();
    if (where.kind === 'conversation') navigate(`/app/@me/${where.conversationId}`);
    else if (where.threadParentId) navigate(`/app/${where.serverId}/${where.threadParentId}/threads/${where.channelId}`);
    else navigate(`/app/${where.serverId}/${where.channelId}`);
    requestJump(targetKey(message), message.id);
  };

  const voice = message.attachments[0]?.durationMs != null;
  const place = where.kind === 'channel' ? `#${where.channelName} · ${where.serverName}` : where.name;

  return (
    <li className="group relative rounded-xl border border-transparent px-3 py-2.5 transition-colors hover:border-line hover:bg-hover/40">
      <button type="button" onClick={jump} className="block w-full text-left" aria-label={`Jump to ${author?.displayName ?? 'the'} message in ${place}`}>
        <p className="mb-1 truncate pr-16 text-[11px] font-bold tracking-wide text-fg-muted uppercase">{place}</p>
        <div className="flex gap-2.5">
          <Avatar user={author} size={28} />
          <div className="min-w-0 flex-1">
            <p className="flex items-baseline gap-2">
              <span className="truncate text-sm font-semibold">{author?.displayName ?? 'Deleted User'}</span>
              <span className="shrink-0 text-[11px] text-fg-muted">{formatMessageTime(message.createdAt)}</span>
            </p>
            {message.content && (
              <MessageContent
                content={message.content.length > 300 ? `${message.content.slice(0, 300)}…` : message.content}
                serverId={message.serverId}
                interactive={false}
                className="line-clamp-3 text-sm text-fg-soft"
              />
            )}
            {message.attachments.length > 0 && (
              <p className="mt-0.5 text-xs text-fg-muted">{voice ? '🎤 Voice message' : `📎 ${message.attachments.length} attachment${message.attachments.length === 1 ? '' : 's'}`}</p>
            )}
            {item.remindAt && (
              <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-accent-soft px-2 py-0.5 text-[11px] font-semibold text-accent-ink">
                <AlarmClock size={12} /> {formatReminder(item.remindAt)}
              </span>
            )}
          </div>
        </div>
      </button>
      <div className="absolute top-1.5 right-1.5 flex opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100">
        <IconButton label={item.remindAt ? 'Change reminder' : 'Remind me'} size="sm" onClick={() => openModal({ type: 'remindMe', message })}>
          <AlarmClock size={15} />
        </IconButton>
        <IconButton label="Remove from saved" size="sm" onClick={() => void toggleSaved(message)}>
          <BookmarkFilled size={15} />
        </IconButton>
      </div>
    </li>
  );
}
